import { Form, Input, ModalProps, Radio, Space } from 'antd';
import Modal from 'app/components/Modal';
import Select from 'app/components/Select';
import { Title } from 'app/components/Typography/Title';
import React from 'react';
import FormItemSections from 'app/pages/TemplateManagment/components/FormItemSections';
import ModalForm from 'app/pages/TemplateManagment/components/ModalForm';
import styled from '@emotion/styled';
import { InspectionCriteriaContext } from '../../Steps/InspectionCriteria';
import {
  ITemplate,
  TemplateContext
} from 'app/pages/TemplateManagment/context';

const StyledForm = styled(Form)`
  padding: 16px;
  > .ant-list {
    margin-top: 27px;
  }
`;
/**
 * ModalEditInspectionTemplate is special form, should not use dynamic form.
 */
const ModalEditInspectionTemplate = (
  props: Partial<Omit<ModalProps, 'onOk'>> & {
    onOk: (values: any) => void;
    selectedTemplate?: {
      id: string | number;
      name: string;
      scheme: string;
      inspectionOption: string;
    } | null;
    templates: Array<{
      id: string | number;
      name: string;
      scheme: string;
    }>;
  }
) => {
  const { fetchDetail, detail } = React.useContext(TemplateContext);
  const {
    templates,
    onLoadMore
  }: {
    templates: any[];
    onLoadMore: (e: React.UIEvent<HTMLDivElement, UIEvent>) => void;
  } = React.useContext(InspectionCriteriaContext);

  const [visibleModal, setVisibleModal] = React.useState('');
  const [editInspectionForm] = Form.useForm();
  const { selectedTemplate, onOk, ...rest } = props;

  const options = React.useMemo(() => {
    if (!templates || !templates.length) {
      return [];
    }
    return templates.map(({ id, name, isDefault }) => {
      return {
        value: id,
        label: `${name} ${isDefault ? '(Default Template)' : ''}`
      };
    });
  }, [templates]);

  const onChangeTemplate = async () => {
    const selected = editInspectionForm.getFieldValue('template');
    if (!fetchDetail) return;

    const response: any = await fetchDetail(selected);
    const { data } = response as { data: ITemplate };
    if (data) {
      const parsed = JSON.parse(`${data.scheme.replace(/'/g, '')}`);
      //hack to set name when go back.
      editInspectionForm.setFieldValue('name', parsed?.name);
      editInspectionForm.setFieldValue('scheme', parsed);
    }
    return [];
  };

  const fetchSelectedTemplate = async (templateId: string) => {
    if (!fetchDetail) return;

    const response: any = await fetchDetail(templateId);
    const { data } = response as { data: ITemplate };
    if (data) {
      const parsed = JSON.parse(`${data.scheme.replace(/'/g, '')}`);
      //hack to set name when go back.
      setTimeout(() => {
        editInspectionForm.setFieldValue('name', data?.name);
        editInspectionForm.setFieldValue('scheme', parsed);
        editInspectionForm.setFieldValue('template', {
          value: data.id,
          label: data.name
        });
      }, 250);
    }
  };

  React.useEffect(() => {
    if (selectedTemplate?.id) {
      fetchSelectedTemplate(selectedTemplate?.id as string);
      return;
    }
    if (selectedTemplate?.scheme) {
      const parsed = JSON.parse(`${selectedTemplate.scheme.replace(/'/g, '')}`);
      //hack to set name when go back.
      setTimeout(() => {
        editInspectionForm.setFieldValue('name', selectedTemplate?.name);
        editInspectionForm.setFieldValue('scheme', parsed);
      }, 250);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  return (
    <Modal
      title="Edit Template :"
      width="80vw"
      centered
      onOk={() => {
        editInspectionForm.submit();
      }}
      {...rest}
    >
      <Form.Provider
        onFormFinish={(name, info) => {
          if (name !== 'edit-inspection') {
            const values = editInspectionForm.getFieldValue('scheme');
            editInspectionForm.setFieldValue(
              'scheme',
              values.reduce((acc: any, current: any) => {
                if (current.name === name) {
                  acc.push({ ...current, ...info.values });
                  return acc;
                }
                acc.push(current);
                return acc;
              }, [])
            );
          }
        }}
      >
        <StyledForm
          layout="vertical"
          onFinish={(values: any) => {
            if (onOk) {
              const name =
                values?.name ||
                options.find(({ value }) => value === values?.template)?.label;
              onOk({ ...values, name });
            }
          }}
          onFieldsChange={(changedFields) => {
            const fieldTemplate = changedFields.find((field) =>
              (field.name as string[]).includes('template')
            );
            if (fieldTemplate) {
              onChangeTemplate();
            }
          }}
          name="edit-inspection"
          form={editInspectionForm}
        >
          <Title level={4}>Inspection Criteria Template:</Title>
          <Form.Item label={'Select Template'} name="template">
            <Select onPopupScroll={onLoadMore} options={options}></Select>
          </Form.Item>
          <Form.Item
            rules={[
              {
                type: 'string',
                required: true,
                message: 'This field is required'
              }
            ]}
            label=""
            name="inspectionOption"
          >
            <Radio.Group>
              <Space>
                <Radio value="save_as_new">Save as new template</Radio>
                <Radio value="only_apply_this_job">
                  Only apply to this job
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {(formInstance) => {
              const inspectionOpt =
                formInstance.getFieldValue('inspectionOption');
              const shouldHidden =
                !inspectionOpt || inspectionOpt === 'only_apply_this_job';

              return (
                <Form.Item
                  style={shouldHidden ? { height: 0 } : {}}
                  rules={
                    shouldHidden
                      ? []
                      : [
                          {
                            type: 'string',
                            required: true,
                            message: 'This field was required'
                          }
                        ]
                  }
                  required
                  hidden={shouldHidden}
                  label="New Template Name"
                  name="name"
                >
                  <Input></Input>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item label="Criteria:" shouldUpdate>
            {(instance) => {
              return (
                <FormItemSections
                  loading={detail?.isLoading || detail?.isFetching}
                  onEdit={(sectionName: string) => {
                    setVisibleModal(sectionName);
                  }}
                  value={instance.getFieldValue('scheme')}
                ></FormItemSections>
              );
            }}
          </Form.Item>
          <Form.Item shouldUpdate>
            {(instance) => {
              return (
                <Form.List name="scheme">
                  {(fields) => {
                    return fields.map((field) => {
                      const section =
                        instance.getFieldValue('scheme')[field.key];
                      return (
                        visibleModal && (
                          <ModalForm
                            name={section.name}
                            configs={section.configs}
                            key={`scheme-${field.key}`}
                            title={section.value}
                            initialValues={{
                              value: section.value,
                              fields: section.fields,
                              configs: section.configs
                            }}
                            onCancel={() => {
                              setVisibleModal('');
                            }}
                            open={section.name === visibleModal}
                          ></ModalForm>
                        )
                      );
                    });
                  }}
                </Form.List>
              );
            }}
          </Form.Item>
        </StyledForm>
      </Form.Provider>
    </Modal>
  );
};

export default ModalEditInspectionTemplate;
